/**
 * This module takes charge of the user input via gamepad (Gamepad API).
 *
 * It is written for two Bluetooth Xbox One controllers paired to an Android
 * phone and played in Chrome, but it deliberately does not hard-code that
 * assumption: the "standard" mapping is used when the browser reports it, and
 * a set of fallbacks (D-pad buttons, hat-switch axis, left analog stick) is
 * used when it does not.
 *
 * The raw state this module reads can be inspected on screen with the
 * `?debug=1` overlay (see "gamepad_debug_overlay.js"), because the target
 * device has no developer console available.
 */
'use strict';
import { PikaUserInput } from './physics.js';

/**
 * Analog sticks rest slightly off-centre, so anything below this magnitude is
 * treated as no input.
 * @constant @type {number}
 */
export const AXIS_DEADZONE = 0.35;

/**
 * Button indices treated as "power hit / select": the four face buttons, the
 * two shoulder buttons and the two triggers of the standard mapping.
 *
 * This is intentionally generous. Any of them works, so a controller that
 * reports a shifted button layout is still playable without a code change.
 * The menu/view buttons (8, 9), the stick clicks (10, 11), the D-pad (12-15)
 * and the guide button (16) are excluded.
 * @constant @type {number[]}
 */
export const POWER_HIT_BUTTONS = [0, 1, 2, 3, 4, 5, 6, 7];

/**
 * D-pad button indices in the standard mapping.
 * @constant
 */
const DPAD = { up: 12, down: 13, left: 14, right: 15 };

/**
 * Read one gamepad and derive the directional and power hit state from it.
 *
 * Three direction sources are combined, so whichever one the controller
 * actually reports takes effect:
 *  1) D-pad buttons (standard mapping)
 *  2) hat-switch axis (some controllers report the D-pad as a single axis
 *     instead of as buttons; only consulted when there are no D-pad buttons)
 *  3) left analog stick
 *
 * @param {Gamepad} pad
 * @returns {{xDirection: number, yDirection: number, powerHitIsDown: boolean, dpadSource: string}}
 */
export function readGamepad(pad) {
  const buttons = pad.buttons || [];
  const axes = pad.axes || [];

  let x = 0;
  let y = 0;
  let dpadSource = 'none';

  // 1) D-pad as buttons.
  const hasDpadButtons = buttons.length > DPAD.right;
  if (hasDpadButtons) {
    if (isPressed(buttons[DPAD.left])) {
      x = -1;
    } else if (isPressed(buttons[DPAD.right])) {
      x = 1;
    }
    if (isPressed(buttons[DPAD.up])) {
      y = -1;
    } else if (isPressed(buttons[DPAD.down])) {
      y = 1;
    }
    if (x !== 0 || y !== 0) {
      dpadSource = 'dpad-buttons';
    }
  }

  // 2) D-pad as a hat-switch axis. Only when the controller does not expose
  //    D-pad buttons at all, otherwise a controller that reports both would
  //    get contradictory readings.
  if (x === 0 && y === 0 && !hasDpadButtons && axes.length > 9) {
    const hat = readHatAxis(axes[9]);
    if (hat !== null) {
      x = hat.x;
      y = hat.y;
      dpadSource = 'hat-axis(9)';
    }
  }

  // 3) Left analog stick.
  if (x === 0 && y === 0 && axes.length >= 2) {
    const ax = axes[0];
    const ay = axes[1];
    if (Math.abs(ax) >= AXIS_DEADZONE) {
      x = ax < 0 ? -1 : 1;
    }
    if (Math.abs(ay) >= AXIS_DEADZONE) {
      y = ay < 0 ? -1 : 1;
    }
    if (x !== 0 || y !== 0) {
      dpadSource = 'left-stick';
    }
  }

  let powerHitIsDown = false;
  for (const i of POWER_HIT_BUTTONS) {
    if (i < buttons.length && isPressed(buttons[i])) {
      powerHitIsDown = true;
      break;
    }
  }

  return { xDirection: x, yDirection: y, powerHitIsDown, dpadSource };
}

/**
 * Whether a button entry counts as pressed. Older browsers expose the button
 * as a bare number instead of a GamepadButton object, and analog triggers can
 * report `pressed === false` while resting at a non-zero value.
 * @param {GamepadButton|number} button
 * @returns {boolean}
 */
function isPressed(button) {
  if (button === undefined || button === null) {
    return false;
  }
  if (typeof button === 'number') {
    return button > 0.5;
  }
  return button.pressed === true || button.value > 0.5;
}

/**
 * Decode a hat-switch axis value into a direction.
 *
 * A hat axis reports eight directions spread over -1..1 and a resting value
 * outside that range (commonly 1.286 or 3.286). The value is matched to the
 * nearest of the eight directions within a tolerance.
 * @param {number} value
 * @returns {{x: number, y: number}|null} null when resting or unrecognised
 */
function readHatAxis(value) {
  if (typeof value !== 'number' || Number.isNaN(value) || value > 1.05) {
    return null;
  }
  /** @type {Array<[number, number, number]>} [axis value, x, y] */
  const table = [
    [-1.0, 0, -1], // up
    [-5 / 7, 1, -1], // up-right
    [-3 / 7, 1, 0], // right
    [-1 / 7, 1, 1], // down-right
    [1 / 7, 0, 1], // down
    [3 / 7, -1, 1], // down-left
    [5 / 7, -1, 0], // left
    [1.0, -1, -1], // up-left
  ];
  for (const [v, x, y] of table) {
    if (Math.abs(value - v) < 0.1) {
      return { x, y };
    }
  }
  return null;
}

/**
 * Class holding the gamepad state shared by both players.
 *
 * `navigator.getGamepads()` returns a fresh snapshot each call, so it is read
 * once per game frame and the result is reused by both player slots and by the
 * debug overlay.
 */
class GamepadManager {
  constructor() {
    /** @type {Gamepad[]} connected gamepads, ordered by their Gamepad.index */
    this.snapshot = [];
    /** @type {boolean} swap which physical controller drives which player */
    this.swapped = false;
    /** @type {string[]} recent connect/disconnect events, newest last */
    this.eventLog = [];
    /** @type {string|null} last error thrown by navigator.getGamepads() */
    this.lastError = null;

    window.addEventListener('gamepadconnected', (event) => {
      // @ts-ignore GamepadEvent
      this.log(`connected  #${event.gamepad.index} ${event.gamepad.id}`);
    });
    window.addEventListener('gamepaddisconnected', (event) => {
      // @ts-ignore GamepadEvent
      this.log(`disconnected #${event.gamepad.index} ${event.gamepad.id}`);
    });
  }

  /**
   * Append a line to the on-screen event log, keeping only the recent ones.
   * @param {string} message
   */
  log(message) {
    this.eventLog.push(message);
    if (this.eventLog.length > 6) {
      this.eventLog.shift();
    }
  }

  /**
   * Re-read the gamepad snapshot. Call once per frame, before reading input.
   */
  refresh() {
    /** @type {Gamepad[]} */
    const pads = [];
    try {
      const raw = navigator.getGamepads ? navigator.getGamepads() : [];
      for (const pad of raw) {
        if (pad && pad.connected) {
          pads.push(pad);
        }
      }
      pads.sort((a, b) => a.index - b.index);
      this.lastError = null;
    } catch (err) {
      this.lastError = String(err);
    }
    this.snapshot = pads;
  }

  /**
   * The gamepad currently driving the given player slot, if any.
   * @param {number} slot 0 for player 1, 1 for player 2
   * @returns {Gamepad|null}
   */
  getPadForSlot(slot) {
    const effective = this.swapped ? 1 - slot : slot;
    return this.snapshot[effective] || null;
  }

  /**
   * Swap which physical controller drives which player.
   */
  toggleSwap() {
    this.swapped = !this.swapped;
    this.log(`slots ${this.swapped ? 'swapped' : 'normal'}`);
  }
}

/** The single shared gamepad manager. */
export const gamepadManager = new GamepadManager();

/**
 * Class representing a gamepad used to control a player.
 *
 * A slot is a logical player position, not a physical controller: slot 0 is
 * whichever connected controller comes first, slot 1 the second. A slot with
 * no controller behind it simply reports no input, which is what keeps the
 * keyboard-only case working unchanged.
 */
export class PikaGamepad extends PikaUserInput {
  /**
   * @param {number} slot 0 for player 1, 1 for player 2
   */
  constructor(slot) {
    super();

    /** @type {number} */
    this.slot = slot;
    /** @type {boolean} */
    this.powerHitIsDownPrevious = false;
    /** @type {boolean} whether a controller is behind this slot right now */
    this.connected = false;
    /** @type {string} which input source produced the current direction */
    this.dpadSource = 'none';
  }

  /**
   * Get xDirection, yDirection, powerHit input from the gamepad.
   * This method is for freezing the gamepad input during the process of one
   * game frame, mirroring PikaKeyboard.getInput().
   */
  getInput() {
    const pad = gamepadManager.getPadForSlot(this.slot);
    if (pad === null) {
      this.connected = false;
      this.dpadSource = 'none';
      this.xDirection = 0;
      this.yDirection = 0;
      this.powerHit = 0;
      this.powerHitIsDownPrevious = false;
      return;
    }

    this.connected = true;
    const state = readGamepad(pad);
    this.xDirection = state.xDirection;
    this.yDirection = state.yDirection;
    this.dpadSource = state.dpadSource;

    // Power hit is edge-triggered: only the frame the button goes down counts,
    // same as the keyboard, otherwise holding the button auto-repeats.
    if (!this.powerHitIsDownPrevious && state.powerHitIsDown) {
      this.powerHit = 1;
    } else {
      this.powerHit = 0;
    }
    this.powerHitIsDownPrevious = state.powerHitIsDown;
  }
}
